import { useState, useEffect, useRef } from 'react'

export default function VideoCallModal({ isOpen, onClose, callType, otherUser, currentUser }) {
  const [isMuted, setIsMuted] = useState(false)
  const [isVideoOff, setIsVideoOff] = useState(callType === 'voice')
  const [isScreenSharing, setIsScreenSharing] = useState(false)
  const [callDuration, setCallDuration] = useState(0)
  const [isConnecting, setIsConnecting] = useState(true)

  const localVideoRef = useRef(null)
  const remoteVideoRef = useRef(null)
  const localStreamRef = useRef(null)
  const timerRef = useRef(null)

  useEffect(() => {
    if (isOpen) {
      startCall()
      // Start call timer
      timerRef.current = setInterval(() => {
        setCallDuration((prev) => prev + 1)
      }, 1000)
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
      cleanup()
    }
  }, [isOpen])

  const startCall = async () => {
    try {
      // Get user media (camera and/or microphone)
      const stream = await navigator.mediaDevices.getUserMedia({
        video: callType === 'video',
        audio: true
      })

      localStreamRef.current = stream

      if (localVideoRef.current && callType === 'video') {
        localVideoRef.current.srcObject = stream
      }

      // Simulate connection (in real app, would use WebRTC/LiveKit)
      setTimeout(() => {
        setIsConnecting(false)
      }, 2000)
    } catch (err) {
      console.error('Error accessing media devices:', err)
      alert('Could not access camera/microphone. Please check permissions.')
      onClose()
    }
  }

  const cleanup = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop())
    }
  }

  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0]
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled
        setIsMuted(!audioTrack.enabled)
      }
    }
  }

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0]
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled
        setIsVideoOff(!videoTrack.enabled)
      }
    }
  }

  const toggleScreenShare = async () => {
    if (!isScreenSharing) {
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: true
        })

        // Replace video track with screen share
        const screenTrack = screenStream.getVideoTracks()[0]
        const sender = localStreamRef.current.getVideoTracks()[0]

        if (localVideoRef.current) {
          localVideoRef.current.srcObject = screenStream
        }

        screenTrack.onended = () => {
          setIsScreenSharing(false)
          // Restore camera
          if (localStreamRef.current && localVideoRef.current) {
            localVideoRef.current.srcObject = localStreamRef.current
          }
        }

        setIsScreenSharing(true)
      } catch (err) {
        console.error('Error sharing screen:', err)
      }
    } else {
      // Stop screen sharing
      if (localStreamRef.current && localVideoRef.current) {
        localVideoRef.current.srcObject = localStreamRef.current
      }
      setIsScreenSharing(false)
    }
  }

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const handleEndCall = () => {
    cleanup()
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[100] bg-gjyl-slate-900 flex flex-col">
      {/* Header */}
      <div className="h-16 glass-strong border-b border-gjyl-slate-700 px-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img
            src={otherUser?.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${otherUser?.id}`}
            alt={otherUser?.username}
            className="w-10 h-10 rounded-gjyl"
          />
          <div>
            <h3 className="text-white font-semibold">{otherUser?.username}</h3>
            <p className="text-sm text-slate-400">
              {isConnecting ? 'Connecting...' : formatDuration(callDuration)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Call Quality Indicator */}
          <div className="flex items-center gap-2 px-3 py-1.5 glass rounded-gjyl">
            <div className="w-2 h-2 bg-gjyl-success rounded-full"></div>
            <span className="text-xs text-slate-400">HD</span>
          </div>
        </div>
      </div>

      {/* Video Area */}
      <div className="flex-1 relative bg-gjyl-darker">
        {/* Remote Video (Main) */}
        <div className="absolute inset-0 flex items-center justify-center">
          {isConnecting ? (
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gjyl-indigo-500 mx-auto mb-4"></div>
              <p className="text-white">Connecting to {otherUser?.username}...</p>
            </div>
          ) : callType === 'voice' ? (
            <div className="text-center">
              <img
                src={otherUser?.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${otherUser?.id}`}
                alt={otherUser?.username}
                className="w-32 h-32 rounded-full mx-auto mb-4 ring-4 ring-gjyl-indigo-500"
              />
              <h3 className="text-2xl font-semibold text-white mb-2">{otherUser?.username}</h3>
              <p className="text-slate-400">Voice call in progress</p>

              {/* Voice Activity Indicator */}
              <div className="mt-8 flex items-center justify-center gap-2">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="w-3 bg-gjyl-indigo-500 rounded-full animate-pulse"
                    style={{
                      height: `${Math.random() * 40 + 20}px`,
                      animationDelay: `${i * 0.1}s`
                    }}
                  />
                ))}
              </div>
            </div>
          ) : (
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover"
            />
          )}
        </div>

        {/* Local Video (Picture-in-Picture) */}
        {callType === 'video' && !isVideoOff && (
          <div className="absolute top-4 right-4 w-64 h-48 rounded-gjyl-xl overflow-hidden border-2 border-gjyl-slate-700 shadow-gjyl-lift-strong bg-gjyl-slate-800">
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover mirror"
            />
            <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/60 backdrop-blur-sm rounded text-xs text-white">
              You {isScreenSharing && '(Sharing)'}
            </div>
          </div>
        )}

        {/* Video Off Indicator */}
        {callType === 'video' && isVideoOff && (
          <div className="absolute top-4 right-4 w-64 h-48 rounded-gjyl-xl border-2 border-gjyl-slate-700 bg-gjyl-slate-800 flex items-center justify-center">
            <div className="text-center">
              <img
                src={currentUser?.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${currentUser?.id}`}
                alt="You"
                className="w-16 h-16 rounded-full mx-auto mb-2"
              />
              <p className="text-xs text-slate-400">Camera Off</p>
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="h-24 glass-strong border-t border-gjyl-slate-700 px-6 flex items-center justify-center">
        <div className="flex items-center gap-4">
          {/* Microphone */}
          <button
            onClick={toggleMute}
            className={`w-14 h-14 rounded-full flex items-center justify-center transition-all hover:scale-110 ${
              isMuted
                ? 'bg-gjyl-danger hover:bg-gjyl-danger/80'
                : 'glass hover:glass-strong'
            }`}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMuted ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              )}
            </svg>
          </button>

          {/* Camera (Video calls only) */}
          {callType === 'video' && (
            <button
              onClick={toggleVideo}
              className={`w-14 h-14 rounded-full flex items-center justify-center transition-all hover:scale-110 ${
                isVideoOff
                  ? 'bg-gjyl-danger hover:bg-gjyl-danger/80'
                  : 'glass hover:glass-strong'
              }`}
              title={isVideoOff ? 'Turn on camera' : 'Turn off camera'}
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isVideoOff ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                )}
              </svg>
            </button>
          )}

          {/* Screen Share (Video calls only) */}
          {callType === 'video' && (
            <button
              onClick={toggleScreenShare}
              className={`w-14 h-14 rounded-full flex items-center justify-center transition-all hover:scale-110 ${
                isScreenSharing
                  ? 'bg-gjyl-indigo-500 hover:bg-gjyl-indigo-400'
                  : 'glass hover:glass-strong'
              }`}
              title={isScreenSharing ? 'Stop sharing' : 'Share screen'}
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </button>
          )}

          {/* End Call */}
          <button
            onClick={handleEndCall}
            className="w-16 h-16 rounded-full bg-gjyl-danger hover:bg-gjyl-danger/80 flex items-center justify-center transition-all hover:scale-110"
            title="End call"
          >
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z" />
            </svg>
          </button>

          {/* Settings */}
          <button
            className="w-14 h-14 rounded-full glass hover:glass-strong flex items-center justify-center transition-all hover:scale-110"
            title="Call settings"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Mirror CSS for local video */}
      <style>{`
        .mirror {
          transform: scaleX(-1);
        }
      `}</style>
    </div>
  )
}
